import { type FC, useEffect } from 'react';
import { useAppStore } from '@/stores/app-store';
import { ScenarioBuilder } from './ScenarioBuilder';
import { SimulationDashboard } from './SimulationDashboard';
import type { SimulationScenario } from '@/types/simulation';

// ---------------------------------------------------------------------------
// SimulationModal — full-screen overlay, z-[200]
// Routes between ScenarioBuilder and SimulationDashboard
// ---------------------------------------------------------------------------

export const SimulationModal: FC = () => {
  const simulation = useAppStore((s) => s.simulation);
  const setSimulationActive = useAppStore((s) => s.setSimulationActive);
  const setSimulationScenario = useAppStore((s) => s.setSimulationScenario);
  const setSimStatus = useAppStore((s) => s.setSimStatus);
  const resetSimulation = useAppStore((s) => s.resetSimulation);

  // Close on Escape
  useEffect(() => {
    if (!simulation.active) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') handleClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [simulation.active]);

  if (!simulation.active) return null;

  const handleClose = () => {
    resetSimulation();
    setSimulationActive(false);
  };

  const handleLaunch = (scenario: SimulationScenario) => {
    setSimulationScenario(scenario);
    setSimStatus('building');
  };

  return (
    <div className="fixed inset-0 z-[200] bg-gray-950/98 flex flex-col">
      {simulation.status === 'idle' || !simulation.scenario ? (
        // Step 1: Scenario builder
        <div className="flex-1 flex items-start justify-center overflow-y-auto py-8 px-4">
          <div className="w-full max-w-3xl bg-gray-900/80 border border-slate-700/60 rounded-xl overflow-hidden shadow-2xl">
            <ScenarioBuilder onLaunch={handleLaunch} onClose={handleClose} />
          </div>
        </div>
      ) : (
        // Step 2: Simulation game view (full-screen)
        <SimulationDashboard onClose={handleClose} />
      )}
    </div>
  );
};
